/**
 * Document registry (frontend) — one definition per document type for VIEW
 * concerns: which React component renders it, and where its live record lives
 * in the app ("Open in KrewUp").
 *
 * Capabilities (pdf / shareLink / email / print) are NOT duplicated here — the
 * backend is the single source of truth and returns them in the /api/shared
 * response. The viewer reads them from there. This registry only maps a type to
 * how it's displayed, so the two runtimes can never disagree on capabilities.
 */
import type { ComponentType } from 'react';

export interface DocumentCapabilities {
  pdf?: boolean;
  shareLink?: boolean;
  email?: boolean;
  print?: boolean;
}

export interface DocumentProps {
  payload: any;
  docType: string;
  docId: string;
  mode: 'view' | 'print';
  capabilities: DocumentCapabilities;
}

export type DocumentComponent = ComponentType<DocumentProps>;

export interface FrontendDocumentDefinition {
  docType: string;
  renderer: DocumentComponent;
  /** In-app route to open the live record (members only). */
  appRoute?: string;
}

const defs = new Map<string, FrontendDocumentDefinition>();

export function registerDocument(def: FrontendDocumentDefinition): void {
  defs.set(def.docType, def);
}

export function getDocumentDefinition(docType: string): FrontendDocumentDefinition | null {
  return defs.get(docType) || null;
}

/**
 * Returns the renderer for the given docType, or null if none is registered.
 * The viewer is responsible for showing an explicit error in that case — we
 * never fall back to a generic layout, so a missing renderer is loud, not
 * silently degraded.
 */
export function getDocumentComponent(docType: string): DocumentComponent | null {
  return defs.get(docType)?.renderer || null;
}

export function getDocumentAppRoute(docType: string): string | null {
  return defs.get(docType)?.appRoute || null;
}
